import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import Button from '../../../components/common/Button';
import DatePickerField from '../../../components/common/DatePickerField';
import SelectField from '../../../components/common/SelectField';
import TextField from '../../../components/common/TextField';
import ApplicantTable from '../../../components/module/rec/applicant/ApplicantTable';
// Process status grid columns + history popup: ApplicantTable (PROCESS_STATUS_GRID_AND_POPUP_FRONTEND.md)
import { DEFAULT_ROWS_PER_PAGE } from '../../../utils/constants';
import { useLocation } from 'react-router-dom';
import { APPLICANT_STATUSES } from '../../../utils/common';

type DateRangeType = 'all' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'last30Days' | 'thisCalendarYear' | 'oneYear' | 'custom';

interface ApplicantTableProps {
  status: string | null;
  source: string | null;
  vacancyCode: string;
  vacancyOrName: string;
  issueId: string | null;
  startDate?: string;
  endDate?: string;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

const ApplicantSearch: React.FC<{ userEmail?: string }> = ({ userEmail }) => {
  const [page, setPage] = useState(0);
  const { state } = useLocation();
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { code = '', status = '' } = state || {};
  // Check if coming from VacancySearch (has code) - use "all" filter, otherwise use "this month"
  const isFromVacancySearch = !!code;
  // Initialize with "This Month" dates
  const getThisMonthRange = () => {
    const today = dayjs();
    return {
      start: today.startOf('month').format('YYYY-MM-DD'),
      end: today.endOf('month').format('YYYY-MM-DD'),
    };
  };

  const initialThisMonthRange = getThisMonthRange();
  // If coming from VacancySearch, use "all" (empty dates), otherwise use "this month"
  const initialDateRangeType = isFromVacancySearch ? 'all' : 'thisMonth';
  const initialStartDate = isFromVacancySearch ? '' : initialThisMonthRange.start;
  const initialEndDate = isFromVacancySearch ? '' : initialThisMonthRange.end;
  
  const [filterParams, setFilterParams] = useState<Omit<ApplicantTableProps, 'page' | 'rowsPerPage' | 'onPageChange' | 'onRowsPerPageChange'>>({
    status: status,
    source: '',
    vacancyCode: code,
    vacancyOrName: '',
    issueId: '',
    startDate: initialStartDate,
    endDate: initialEndDate,
  });
  const defaultYear = dayjs().year().toString();
  const [searchParams, setSearchParams] = useState({
    name: '',
    vacancy: code,
    source: '',
    status: status,
    issueId: '',
    dateRangeType: initialDateRangeType as DateRangeType,
    selectedYear: defaultYear,
    startDate: initialStartDate,
    endDate: initialEndDate,
  });
  const [dateRangeError, setDateRangeError] = useState('');
  const calendarYearOptions = useMemo(() => {
    const latestYear = dayjs().year();
    const options: { value: string; label: string }[] = [];
    for (let year = latestYear; year >= 2010; year--) {
      options.push({ value: year.toString(), label: year.toString() });
    }
    return options;
  }, []);

  const getCalendarYearRange = (year: string) => ({
    start: dayjs(`${year}-01-01`).format('YYYY-MM-DD'),
    end: dayjs(`${year}-12-31`).format('YYYY-MM-DD'),
  });
  const dateRangeOptions = useMemo(
    () => [
      { value: 'thisWeek', label: 'This Week' },
      { value: 'lastWeek', label: 'Last Week' },
      { value: 'thisMonth', label: 'This Month' },
      { value: 'lastMonth', label: 'Last Month' },
      { value: 'last30Days', label: 'Last 30 Days' },
      { value: 'thisCalendarYear', label: 'This Calendar Year' },
      { value: 'oneYear', label: 'One Year' },
      { value: 'all', label: 'All' },
      { value: 'custom', label: 'Custom' },
    ],
    []
  );

  const handleDateRangeTypeChange = (value: DateRangeType) => {
    setDateRangeError('');
    setSearchParams((prev) => {
      const updatedParams = { ...prev, dateRangeType: value };

      if (value === 'all') {
        updatedParams.startDate = '';
        updatedParams.endDate = '';
      }

      if (value === 'thisWeek') {
        const today = dayjs();
        // Get Monday of current week (day 1 = Monday)
        const dayOfWeek = today.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert Sunday (0) to 6 days back
        const startOfWeek = today.subtract(daysFromMonday, 'day');
        updatedParams.startDate = startOfWeek.format('YYYY-MM-DD');
        updatedParams.endDate = today.format('YYYY-MM-DD');
      }

      if (value === 'lastWeek') {
        const today = dayjs();
        // Get Monday of current week
        const dayOfWeek = today.day();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const startOfCurrentWeek = today.subtract(daysFromMonday, 'day');
        // Last week: 7 days before start of current week to 1 day before start of current week
        const endOfLastWeek = startOfCurrentWeek.subtract(1, 'day');
        const startOfLastWeek = endOfLastWeek.subtract(6, 'day');
        updatedParams.startDate = startOfLastWeek.format('YYYY-MM-DD');
        updatedParams.endDate = endOfLastWeek.format('YYYY-MM-DD');
      }

      if (value === 'thisMonth') {
        const today = dayjs();
        updatedParams.startDate = today.startOf('month').format('YYYY-MM-DD');
        updatedParams.endDate = today.endOf('month').format('YYYY-MM-DD');
      }

      if (value === 'lastMonth') {
        const today = dayjs();
        const lastMonth = today.subtract(1, 'month');
        updatedParams.startDate = lastMonth.startOf('month').format('YYYY-MM-DD');
        updatedParams.endDate = lastMonth.endOf('month').format('YYYY-MM-DD');
      }

      if (value === 'last30Days') {
        const today = dayjs();
        updatedParams.startDate = today.subtract(29, 'day').format('YYYY-MM-DD');
        updatedParams.endDate = today.format('YYYY-MM-DD');
      }

      if (value === 'thisCalendarYear') {
        const today = dayjs();
        updatedParams.startDate = today.startOf('year').format('YYYY-MM-DD');
        updatedParams.endDate = today.endOf('year').format('YYYY-MM-DD');
      }

      if (value === 'oneYear') {
        const year = prev.selectedYear || defaultYear;
        const { start, end } = getCalendarYearRange(year);
        updatedParams.selectedYear = year;
        updatedParams.startDate = start;
        updatedParams.endDate = end;
      }

      if (value === 'custom') {
        // Keep existing dates, user can edit them
      }

      return updatedParams;
    });
  };

  const handleYearChange = (year: string) => {
    const { start, end } = getCalendarYearRange(year);
    setDateRangeError('');
    setSearchParams((prev) => ({
      ...prev,
      selectedYear: year,
      startDate: start,
      endDate: end,
    }));
  };

  const handleCustomDateChange = (key: 'startDate' | 'endDate', value: string | null) => {
    setDateRangeError('');
    setSearchParams((prev) => ({
      ...prev,
      [key]: value || '',
    }));
  };

  const validateDateRange = () => {
    if (searchParams.dateRangeType === 'all') {
      // All option should have empty dates
      if (dateRangeError) {
        setDateRangeError('');
      }
      return true;
    }

    if (searchParams.dateRangeType !== 'custom') {
      // For non-custom options, dates are auto-calculated, so validation passes
      if (dateRangeError) {
        setDateRangeError('');
      }
      return true;
    }

    // Custom date range validation
    if (!searchParams.startDate || !searchParams.endDate) {
      setDateRangeError('Start and end dates are required.');
      return false;
    }

    if (dayjs(searchParams.startDate).isAfter(dayjs(searchParams.endDate))) {
      setDateRangeError('Start date must be before end date.');
      return false;
    }

    setDateRangeError('');
    return true;
  };

  const handleSearch = () => {
    if (!validateDateRange()) {
      return;
    }

    setFilterParams({
      status: searchParams.status,
      source: searchParams.source,
      vacancyCode: searchParams.vacancy,
      vacancyOrName: searchParams.name,
      issueId: searchParams.issueId,
      startDate: searchParams.startDate,
      endDate: searchParams.endDate,
    });
    // Reset to first page when searching
    setPage(0);
  };

  const handleReset = () => {
    const thisMonthRange = getThisMonthRange();
    setSearchParams({
      name: '',
      vacancy: '',
      source: '',
      status: '',
      issueId: '',
      dateRangeType: 'thisMonth',
      selectedYear: defaultYear,
      startDate: thisMonthRange.start,
      endDate: thisMonthRange.end,
    });
    setFilterParams({
      status: '',
      source: '',
      vacancyCode: '',
      vacancyOrName: '',
      issueId: '',
      startDate: thisMonthRange.start,
      endDate: thisMonthRange.end,
    });
    setDateRangeError('');
    // Reset to first page when clearing
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <Box className="page-container">
      <Typography className="page-title">
        Applicant Search
      </Typography>
      <Box className="search-section">
        <Box className="search-bar" sx={{ display: 'flex', gap: '16px', flexWrap: 'nowrap', width: '100%' }}>
          <Box style={{ flex: 2, minWidth: '250px' }}>
            <TextField
              placeholder="Search By Vacancy/First Name/Last Name"
              value={searchParams.name}
              onChange={(e) => setSearchParams({ ...searchParams, name: e.target.value })}
              width="100%"
            />
          </Box>
          <Box style={{ flex: 1, minWidth: '80px', maxWidth: '120px' }}>
            <TextField
              placeholder="Code"
              width="100%"
              value={searchParams.vacancy}
              onChange={(e) => setSearchParams({ ...searchParams, vacancy: e.target.value as string })}
            />
          </Box>
          <Box style={{ flex: 1, minWidth: '100px', maxWidth: '200px' }}>
            <SelectField
              placeholder="Source"
              width="100%"
              value={searchParams.source}
              onChange={(e) => setSearchParams({ ...searchParams, source: e.target.value as string })}
              options={[
                { value: 'direct', label: 'Direct' },
                { value: 'linkedin', label: 'Linkedin' },
                { value: 'agency', label: 'Agency' },
                { value: 'website', label: 'Website' },
                { value: 'internal referral', label: 'Internal Referral' },
                { value: 'external referral', label: 'External Referral' },
              ]}
            />
          </Box>
          <Box style={{ flex: 1, minWidth: '200px' }}>
            <SelectField
              placeholder="Status"
              value={searchParams.status}
              width="100%"
              onChange={(e) => setSearchParams({ ...searchParams, status: e.target.value as string })}
              options={APPLICANT_STATUSES.map((statusName) => ({
                value: statusName,
                label: statusName
              }))}
            />
          </Box>
          <Box style={{ flex: 1, minWidth: '150px', maxWidth: '200px' }}>
            <SelectField
              placeholder="Date Range"
              width="100%"
              value={searchParams.dateRangeType}
              onChange={(e) => handleDateRangeTypeChange(e.target.value as DateRangeType)}
              options={dateRangeOptions}
            />
          </Box>
          {searchParams.dateRangeType === 'oneYear' && (
            <Box style={{ flex: 1, minWidth: '85px', maxWidth: '100px' }}>
              <SelectField
                placeholder="Select"
                width="100%"
                value={searchParams.selectedYear}
                onChange={(e) => handleYearChange(e.target.value as string)}
                options={calendarYearOptions}
              />
            </Box>
          )}
          <Box style={{ flex: 1, minWidth: '150px', maxWidth: '180px' }}>
            <DatePickerField
              placeholder="Start Date"
              width="100%"
              value={searchParams.startDate ? dayjs(searchParams.startDate) : null}
              onChange={(date) => handleCustomDateChange('startDate', date)}
              required={searchParams.dateRangeType === 'custom'}
              helperText={searchParams.dateRangeType === 'custom' && dateRangeError ? dateRangeError : undefined}
              disabled={searchParams.dateRangeType !== 'custom'}
            />
          </Box>
          <Box style={{ flex: 1, minWidth: '150px', maxWidth: '180px' }}>
            <DatePickerField
              placeholder="End Date"
              width="100%"
              value={searchParams.endDate ? dayjs(searchParams.endDate) : null}
              onChange={(date) => handleCustomDateChange('endDate', date)}
              required={searchParams.dateRangeType === 'custom'}
              helperText={searchParams.dateRangeType === 'custom' && dateRangeError ? dateRangeError : undefined}
              disabled={searchParams.dateRangeType !== 'custom'}
            />
          </Box>
          <Box className="search-bar-button-group">
            <Button variant="contained" onClick={handleSearch} width="120px">
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset} width="120px">
              Clear
            </Button>
          </Box>
        </Box>
        <ApplicantTable
          status={filterParams.status ? filterParams.status : undefined}
          source={filterParams.source ? filterParams.source : undefined}
          vacancyCode={filterParams.vacancyCode ? filterParams.vacancyCode : undefined}
          vacancyOrName={filterParams.vacancyOrName ? filterParams.vacancyOrName : undefined}
          startDate={filterParams.startDate ? filterParams.startDate : undefined}
          endDate={filterParams.endDate ? filterParams.endDate : undefined}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          userEmail={userEmail}
        />
      </Box>
    </Box>
  );
};

export default ApplicantSearch;
